import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { toastError } from "@/lib/errorToast";

export interface SessionExercise {
  name: string;
  sets?: string;
  reps?: string;
  rest?: string;
  notes?: string;
  fact_ids?: string[];
}

export interface SessionRecord {
  id: string;
  user_id: string;
  title: string;
  objective: string;
  exercises: SessionExercise[];
  duration_minutes: number;
  level_required: string;
  contraindications: Array<{ zone: string; reason: string }>;
  warnings: string[];
  fact_ids: string[];
  tags: string[];
  mode: string;
  status: string;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  source_program?: { id: string; title: string } | null;
}

export function useSessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<SessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessionsCount, setSessionsCount] = useState(0);

  const fetchSessions = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const { data, count, error } = await supabase
      .from("sessions")
      .select("*, program_sessions(program_id, programs(id, title))", { count: "exact" })
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (data) {
      const enriched = (data as any[]).map((s) => {
        const link = s.program_sessions?.[0];
        const prog = link?.programs;
        return {
          ...s,
          source_program: prog ? { id: prog.id, title: prog.title } : null,
        } as SessionRecord;
      });
      setSessions(enriched);
      setSessionsCount(count ?? enriched.length);
    }
    if (error) console.error("Sessions fetch error:", error);
    setLoading(false);
  }, [user]);

  const fetchSessionsPage = useCallback(async (page: number, pageSize: number) => {
    if (!user) return;
    setLoading(true);
    const from = (page - 1) * pageSize;
    const to = page * pageSize - 1;
    const { data, count, error } = await supabase
      .from("sessions")
      .select("*, program_sessions(program_id, programs(id, title))", { count: "exact" })
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .range(from, to);
    if (data) {
      const enriched = (data as any[]).map((s) => {
        const link = s.program_sessions?.[0];
        const prog = link?.programs;
        return { ...s, source_program: prog ? { id: prog.id, title: prog.title } : null } as SessionRecord;
      });
      setSessions(enriched);
    }
    setSessionsCount(count || 0);
    if (error) console.error("Sessions page fetch error:", error);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const saveSession = async (session: Omit<SessionRecord, "id" | "user_id" | "created_at" | "updated_at" | "is_favorite">) => {
    if (!user) return null;
    // Ensure we have an active auth session for RLS
    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession) {
      toastError("Session expirée, reconnecte-toi", "Sauvegarder une séance");
      return null;
    }
    const { data, error } = await supabase
      .from("sessions")
      .insert({ ...session, user_id: user.id } as any)
      .select()
      .single();
    if (error) {
      toastError("Erreur de sauvegarde", "Sauvegarder une séance", error.message);
      console.error("Save session error:", error);
      return null;
    }
    await fetchSessions();
    return data as unknown as SessionRecord;
  };

  const updateSessionStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("sessions")
      .update({ status } as any)
      .eq("id", id);
    if (error) toastError("Erreur de mise à jour", "Modifier statut séance", error.message);
    else await fetchSessions();
  };

  const deleteSession = async (id: string) => {
    const { error } = await supabase
      .from("sessions")
      .delete()
      .eq("id", id);
    if (error) toastError("Erreur de suppression", "Supprimer une séance", error.message);
    else {
      toast.success("Séance supprimée");
      await fetchSessions();
    }
  };

  const toggleFavorite = async (id: string, current: boolean) => {
    const { error } = await supabase
      .from("sessions")
      .update({ is_favorite: !current } as any)
      .eq("id", id);
    if (error) toastError("Erreur de mise à jour", "Modifier favori séance", error.message);
    else await fetchSessions();
  };

  const duplicateSession = async (id: string, newTitle?: string, customExercises?: SessionExercise[]) => {
    const source = sessions.find((s) => s.id === id);
    if (!source || !user) return null;
    const { data: { session: authSession } } = await supabase.auth.getSession();
    if (!authSession) {
      toastError("Session expirée, reconnecte-toi", "Dupliquer une séance");
      return null;
    }
    const { data, error } = await supabase
      .from("sessions")
      .insert({
        user_id: user.id,
        title: newTitle || `${source.title} (copie)`,
        objective: source.objective,
        exercises: (customExercises || source.exercises) as any,
        duration_minutes: source.duration_minutes,
        level_required: source.level_required,
        contraindications: source.contraindications as any,
        warnings: source.warnings,
        fact_ids: source.fact_ids,
        tags: source.tags,
        mode: source.mode,
        status: "active",
      } as any)
      .select()
      .single();
    if (error) {
      toastError("Erreur de duplication", "Dupliquer une séance", error.message);
      console.error("Duplicate session error:", error);
      return null;
    }
    toast.success("Séance dupliquée !");
    await fetchSessions();
    return data as unknown as SessionRecord;
  };

  return {
    sessions,
    loading,
    sessionsCount,
    fetchSessions,
    fetchSessionsPage,
    saveSession,
    updateSessionStatus,
    deleteSession,
    toggleFavorite,
    duplicateSession,
  };
}
